<template>
  <div class="product-nav-mobile">
    <div class="info-block">
      <div v-if="!isDualColumnEnabled" class="top-custom">
        <template v-if="!isAddToCartVisibleOnPageNav">
          <div class="product-info">
            <div class="product-name">
              <div class="title-badge-container">
                <p class="title">
                  {{ collectionTitle }}
                  <ProductBadge v-if="product.isNewProduct && !product.onClearance && !product.onSale" class="badge" :type="'new'" />
                </p>
              </div>

              <div v-if="!isProductDiscontinued" class="sale-price">
                <Price v-if="isProductAvailableInGeolocation" :product="product" :price="price" mode="mobile" />
                <AddToCartButton
                  v-if="isProductAvailableInGeolocation"
                  :scroll-first="false"
                  :products="productToCartFormat"
                  @added="onAddToCart"
                  class="add-to-cart-button top-cart-button cart"
                  :isEtaAtRisk="product?.isEtaAtRisk"
                />
                <ProductFavorite
                  v-if="mounted"
                  :product="product"
                  :show-favorite="true"
                  class="round"
                  source="product-page"
                  @onAdd="onFavoriteAdded"
                />
              </div>
            </div>
            <div v-if="isProductDiscontinued" class="product-discontinue">
              <p class="discontinue-msg">Sorry, this product has been retired.</p>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="product-info-container">
            <div class="product-name">
              <p class="title">
                {{ collectionTitle }}
                <ProductBadge v-if="product.isNewProduct && !product.onClearance && !product.onSale" class="badge" :type="'new'" />
              </p>
              <div v-if="!isProductDiscontinued && isProductAvailableInGeolocation" class="sale-price">
                <Price :product="product" :price="price" mode="mobile" />
              </div>
            </div>
            <div class="product-atc-section">
              <AddToCartButton
                v-if="!isProductDiscontinued && isProductAvailableInGeolocation"
                :isEtaAtRisk="product?.isEtaAtRisk"
                :scroll-first="false"
                :products="productToCartFormat"
                :animate="false"
                :class="{ 'has-risky-eta': product?.isEtaAtRisk }"
                class="add-to-cart-button"
                @added="onAddToCart"
              />
              <ProductFavorite :show-favorite="true" :show-favorite-text="false" :product="product" source="product-page" @onAdd="onFavoriteAdded" />
            </div>
          </div>
        </template>
        <div v-if="isProductDiscontinued" class="discontinue-btn">
          <a class="button no-margin uppercase" :href="categoryUrl">Shop All</a>
        </div>
      </div>
      <Divider class="product-info-divider" />
      <div class="product-navbar-mobile">
        <span class="navOverviewMobile" style="margin-left: 20px" @click="scrollToPosition('overview', product)" :class="{ active: activeMenu === 'overview' }"
          >Overview</span
        >
        <span class="navDetailsMobile" @click="scrollToPosition('details', product)" :class="{ active: activeMenu === 'details' }">Details</span>
        <span class="navReviewsMobile" @click="scrollToPosition('reviews', product)" :class="{ active: activeMenu === 'reviews' }">Reviews</span>
        <span
          v-show="showBundle && isProductAvailableInGeolocation"
          class="navBundlesMobile"
          @click="scrollToPosition('bundles', product)"
          :class="{ active: activeMenu === 'bundles' }"
          >Bundle</span
        >
      </div>
    </div>
    <Divider class="product-info-divider" />
  </div>
  <div v-if="!isDualColumnEnabled" class="product-nav-desktop container">
    <div class="product-primary-information">
      <div class="primary-info">
        <div class="product-name">
          <h2 class="title">
            {{ collectionTitle }}
            <ProductBadge v-if="product.isNewProduct && !product.onClearance && !product.onSale" class="badge" :type="'new'" />
          </h2>
        </div>
        <nav style="margin-left: 12px">
          <a class="navOverview" style="margin-left: var(--space-4)" @click="scrollToPosition('overview', product)" :class="{ active: activeMenu === 'overview' }"
            >Overview</a
          >
          <a class="navDetails" @click="scrollToPosition('details', product)" :class="{ active: activeMenu === 'details' }">Details</a>
          <a class="navReviews" @click="scrollToPosition('reviews', product)" :class="{ active: activeMenu === 'reviews' }">Reviews</a>
          <a
            v-show="showBundle && isProductAvailableInGeolocation"
            class="navBundles"
            @click="scrollToPosition('bundles', product)"
            :class="{ active: activeMenu === 'bundles' }"
            >Bundle</a
          >
        </nav>
      </div>
      <div class="product-navbar-desktop">
        <div v-if="!isProductDiscontinued && isProductAvailableInGeolocation" class="sale-price">
          <Price :product="product" :price="price" mode="desktop" />
        </div>
        <p v-else-if="isProductAvailableInGeolocation" class="discontinue-msg" style="margin-right: 20px">Sorry, this product has been retired.</p>
        <div v-if="!isProductDiscontinued && isProductAvailableInGeolocation">
          <AddToCartButton
            :scroll-first="false"
            :products="productToCartFormat"
            :animate="false"
            class="add-to-cart-button"
            @added="onAddToCart"
            :isEtaAtRisk="product?.isEtaAtRisk"
          />
        </div>
        <div v-if="!isProductDiscontinued" style="width: 40px; height: 40px; margin-left: 8px">
          <ProductFavorite
            :show-favorite="true"
            :show-favorite-text="false"
            :product="product"
            class="round"
            source="product-page"
            @onAdd="onFavoriteAdded"
          />
        </div>
        <div v-else class="sale-price">
          <a class="button no-margin uppercase" :href="categoryUrl">Shop {{ categoryName }}</a>
        </div>
      </div>
    </div>
    <Divider style="margin-top: 0px; margin-bottom: 0px; height: 1px" />
  </div>
</template>

<script>
import 'intersection-observer';
import { computed , inject } from 'vue';
import { Divider } from '@/pixel/index';

import AddToCartButton from '@/components/commons/AddToCartButton.vue';
import ProductFavorite from '@/components/product/ProductFavorite.vue';
import ProductBadge from '@/components/commons/ProductBadge.vue';
import Price from './Price.vue';
import Tracking from '@/components/commons/Tracking';
import useLifecycleHook from '@/compositions/useLifecycleHook';
import { showCartSnackbar } from '@/components/commons/CartSnackbar.vue';

export default {
  name: 'ProductPageNav',
  components: {
    Divider,
    AddToCartButton,
    ProductFavorite,
    Price,
    ProductBadge,
  },
  props: {
    isDualColumnEnabled: { type: Boolean, default: false },
    showFavorite: Boolean,
    showBundle: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: null,
    },
    price: {},
    currentNavigationCategory: {},
    productToCartFormat: {
      type: Array,
      required: true,
    },
    isAddToCartVisibleOnPageNav: Boolean,
    visible: {
      type: Object,
      default: () => ({
        overview: false,
        details: false,
        reviews: false,
        bundles: false,
      }),
    },
  },
  emits: ['addToCart'],
  setup(props) {
    const scrollToPosition = inject('scrollToPosition');
    const { mounted } = useLifecycleHook();

    const activeMenu = computed(() => {
      const visible = ['overview', 'details', 'reviews', 'bundles'].filter((section) => props.visible[section]);
      return visible.shift();
    });

    return {
      mounted,
      activeMenu,
      scrollToPosition,
    };
  },

  computed: {
    isProductAvailableInGeolocation() {
      return this.product && this.product.isProductAvailableInGeo;
    },
    collectionTitle() {
      if (this.product && this.product.collection) {
        return this.product.collection.name;
      }
      return '';
    },

    isProductDiscontinued() {
      return this.product && this.product.status === 'DISCONTINUED';
    },

    categoryUrl() {
      const category = this.currentNavigationCategory;
      if (category) {
        return this.$router.resolve({ name: 'browse', params: { id: category.id, slug: `${category.parentSlug}-${category.slug}` } }).href;
      }
      return this.$router.resolve({ name: 'home' }).href;
    },
    categoryName() {
      if (this.currentNavigationCategory) {
        return this.currentNavigationCategory.displayName;
      }
      return '';
    },
  },
  methods: {
    // ...mapActions('product', [
    //   'trackCartProducts', //
    // ]),

    calculateThreshold() {
      if (window.matchMedia('(max-width: 767px)').matches) {
        return 0.1;
      }

      if (window.matchMedia('(max-width: 968px)').matches) {
        return 0.5;
      }

      return [0.5, 1.0];
    },

    onAddToCart(products) {
      this.$emit('addToCart', products);
      // this.trackCartProducts(products);
      showCartSnackbar(products[0]);
      Tracking.track('PDP_Click_Floating_Nav', { element: 'add to cart', productId: products[0].id });
    },

    onFavoriteAdded(productId) {
      Tracking.track('PDP_Click_Floating_Nav', { element: 'favorite', productId });
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.product-nav-mobile {
  @media (min-width: $tablet-breakpoint) {
    display: none;
  }

  .info-block {
    justify-content: space-between;
    z-index: 49;
    height: v-bind(isDualColumnEnabled? '41px': '100px');
    overflow: hidden;

    .top-custom {
      height: 56px;
      display: flex;
      justify-content: space-between;
    }

    .product-page {
      width: 100%;
    }

    .product-info-container {
      display: flex;
      justify-content: space-between;
      padding: 5px 0px;
      width: 100%;
      .product-name {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        letter-spacing: var(--uppercase-letter-spacing);
        padding-left: 20px;
        height: auto;
        .sale-price {
          padding: 0px;
          .prices {
            letter-spacing: 0px;
          }
        }
      }
      .product-atc-section {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        width: auto;
        padding-right: 8px;
        .add-to-cart-container {
          button {
            height: 24px;
            font-size: 12px;
            line-height: 100%;
            letter-spacing: 1.2px;
            text-transform: uppercase;
            text-align: center;
            padding: 0px var(--space-2);
          }
        }
        .favorite {
          .af {
            font-size: 1em;
          }
          &.favorite-tag {
            position: static;
          }
        }
      }
    }

    .product-info {
      display: flex;
      flex-direction: column;
      width: auto;
      .add-to-cart-container {
        @media (max-width: calc($mobile-breakpoint - 1px)) {
          display: none;
        }
        @media (min-width: $mobile-breakpoint) and (max-width: calc($tablet-breakpoint - 1px)) {
          button {
            width: auto;
            height: 24px;
            font-size: 12px;
            line-height: 100%;
            letter-spacing: 1.2px;
            text-transform: uppercase;
            text-align: center;
            padding: 0 var(--space-2);
          }
        }
      }
      .favorite {
        .af {
          font-size: 1em;
        }
        @media (max-width: calc($mobile-breakpoint - 1px)) {
          display: none;
        }
      }
      @media (max-width: calc($mobile-breakpoint - 1px)) {
        width: 100%;
      }
    }

    .product-discontinue {
      display: flex;
      width: 100%;
      justify-content: space-between;
    }

    .discontinue-btn {
      margin-right: 20px;
      align-self: center;
    }
    .discontinue-msg {
      font-size: 12px;
      margin-left: 20px;
    }

    .round {
      right: 20px;
      box-sizing: border-box;
      border-radius: 20px;
      width: 40px;
      height: 40px;
      top: 8px;
      color: var(--grey-medium);
      border: 1px solid var(--grey-light);
    }

    .cart {
      display: flex;
      align-items: center;
      position: absolute;
      height: 40px;
      right: 60px;
      top: 8px;
      flex: none;
      order: 1;
      flex-grow: 0;
      margin: 0px 8px;
    }

    .product-name {
      padding-top: 8px;
      line-height: 125%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding-left: 20px;
      @media (max-width: calc($mobile-breakpoint - 1px)) {
        padding-left: 20px;
        justify-content: space-between;
        align-items: center;
        flex-direction: row;
        height: 56px;
      }
      text-align: left;
      letter-spacing: var(--uppercase-letter-spacing);
      text-transform: uppercase;
      color: #626262;

      .title {
        margin: 0px 0px !important;
      }

      .badge {
        position: inherit;
        float: right;
        margin-left: 8px;
        margin-top: 2px;
      }
    }

    .prices {
      letter-spacing: 0px;
    }

    .product-info-divider {
      margin: v-bind(isDualColumnEnabled? '0px': '4px 0px 0px 0px') !important;
      height: 1px !important;
    }

    .product-navbar-mobile {
      display: flex;
      width: 69.6%;
      height: 44px;
      left: 0px;
      right: 0px;
      @media (max-width: 320px) {
        width: 100%;
        overflow-x: scroll;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }
      }

      span {
        cursor: pointer;
        padding: 8.25px 0px;
        align-self: flex-start;
        flex-grow: 0;
        margin: 0px 10px;
        text-decoration: none;
        border-bottom: 2px solid transparent;
      }

      .active {
        border-bottom: 2px solid var(--coral);
        transition: all 0.35s ease-in-out;
        color: var(--coral);
      }
    }
  }

  .product-info-divider {
    margin: v-bind(isDualColumnEnabled? '0px': '0px 0px') !important;
    height: 1px !important;
  }
}

.product-nav-desktop {
  display: block;

  @media (pointer: none), (pointer: coarse) {
    .primary-info a:hover {
      color: var(--link-color);
    }
  }

  .product-primary-information {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: inherit;
  }

  .primary-info {
    display: flex;
    align-items: center;
    .product-name {
      font-size: 1.5em;
      text-transform: uppercase;

      h2 {
        margin: 0px;
        color: var(--font-color);
      }

      .title {
        font-size: 1em;
        font-family: var(--font-regular);
        line-height: 40px;
        color: var(--font-color);
      }

      .badge {
        position: inherit;
        float: right;
        margin-top: 11px;
        margin-left: 8px;
      }
    }
  }
  .discontinue-msg {
    font-size: 12px;
    margin-left: 8px;
    color: #828282;
    margin-bottom: 0px;
    align-self: center;
  }

  .product-navbar-desktop {
    display: flex;
    width: auto;
    align-items: center;
    overflow: hidden;
    justify-content: space-between;

    .favorite {
      .af {
        font-size: 1em;
      }
    }

    .round {
      height: 40px;
      width: 40px;
      right: auto;
      mix-blend-mode: normal;
      justify-content: center;
      top: auto;
      flex: none;
      flex-grow: 0;
      order: 1;
      color: var(--grey-medium);
      border: 1px solid var(--grey-light);
      box-sizing: border-box;
      border-radius: 20px;
    }
  }

  @media (max-width: calc($tablet-breakpoint - 1px)) {
    display: none;
  }
  .sale-price {
    margin-left: 8px;
    color: var(--accent-color);

    .price.desktop {
      display: flex;
    }
  }
  nav {
    display: flex;
    margin-right: 12px;
  }

  nav a {
    flex-basis: 70%;
    cursor: pointer;
    line-height: 125%;
    margin: 0px 12px;
    padding: 20px 0px;
    border-bottom: 2px solid transparent;
    text-decoration: none;
  }

  a:hover {
    color: var(--coral);
  }
  .active {
    border-bottom: 2px solid coral;
    transition: all 0.35s ease-in-out;
    color: var(--coral);
  }
}
</style>
